import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  Database,
  CreditCard,
  Calendar,
  Mail,
  Wallet,
  Shield,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { authHeaders } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  ok: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5",
  warn: "text-amber-400 border-amber-400/30 bg-amber-400/5",
  error: "text-red-400 border-red-400/30 bg-red-400/5",
};

const STATUS_ICONS = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  error: XCircle,
};

const KEY_ICONS = {
  mongo: Database,
  stripe: CreditCard,
  acuity: Calendar,
  email: Mail,
  paypal: Wallet,
  admin_auth: Shield,
};

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * System Status panel — live operational health of every integration.
 * Shown at the top of the admin dashboard so the team can see at a glance
 * whether Stripe, Acuity, Email and the database are healthy.
 *
 * Hits GET /api/admin/system-status (require_admin).
 */
export default function SystemStatusPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/system-status`, {
        headers: authHeaders(),
      });
      setStatus(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "System status load failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!status && loading) {
    return (
      <div
        data-testid="system-status-loading"
        className="border border-white/5 bg-zinc-950 p-8 text-zinc-400 flex items-center gap-3"
      >
        <Loader2 className="h-5 w-5 animate-spin" /> Checking systems…
      </div>
    );
  }
  if (!status) return null;

  return (
    <section
      data-testid="system-status-panel"
      className="border border-amber-400/20 bg-zinc-950"
    >
      <header className="flex items-center justify-between px-7 py-5 border-b border-white/5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" /> System status
          </p>
          <h2 className="mt-2 font-display font-bold text-xl">
            Operational health
          </h2>
          <p className="mt-1 text-xs text-zinc-500">
            Generated {formatTs(status.generated_at)} · Email provider{" "}
            <span className="uppercase tracking-[0.2em] text-amber-400 font-bold">
              {status.email_provider}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="system-status-refresh"
          className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400 hover:text-amber-400 px-4 py-2.5 text-xs uppercase tracking-[0.25em] font-bold transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-b border-white/5">
        {status.integrations.map((i) => {
          const Icon = KEY_ICONS[i.key] || Activity;
          const StatusIcon = STATUS_ICONS[i.status] || CheckCircle2;
          return (
            <div
              key={i.key}
              data-testid={`system-status-${i.key}`}
              data-status={i.status}
              className={`p-5 border-r last:border-r-0 border-white/5 ${STATUS_COLORS[i.status] || ""}`}
            >
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4 opacity-80" />
                <StatusIcon className="h-3.5 w-3.5" />
              </div>
              <p className="mt-3 text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">
                {i.label}
              </p>
              <p className="mt-1 text-xs leading-snug">{i.detail}</p>
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {i.managed}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 px-7 py-5 text-xs text-zinc-400">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
            Last lead captured
          </p>
          <p
            className="mt-1 text-zinc-200"
            data-testid="system-status-last-lead"
          >
            {formatTs(status.last_lead_at)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
            Last paid payment
          </p>
          <p
            className="mt-1 text-zinc-200"
            data-testid="system-status-last-payment"
          >
            {formatTs(status.last_paid_payment_at)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
            Last email dispatched
          </p>
          <p
            className="mt-1 text-zinc-200"
            data-testid="system-status-last-email"
          >
            {formatTs(status.last_email_at)}
          </p>
        </div>
      </div>

      <div className="border-t border-white/5 px-7 py-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 text-xs">
        {[
          ["Members", "members"],
          ["Enquiries", "enquiries"],
          ["Subscribers", "subscribers"],
          ["Applications", "applications"],
          ["Partnerships", "partnerships"],
          ["Home Ed", "home_education"],
          ["Ubuntu", "retreat_bookings"],
          ["Waitlist", "membership_waitlist"],
          ["Payments", "payments_total"],
          ["Paid", "payments_paid"],
          ["Emails sent", "outbound_emails_total"],
          ["Email fails", "outbound_emails_failed"],
          ["Admin team", "admin_users"],
        ].map(([label, key]) => (
          <div
            key={key}
            data-testid={`system-stat-${key}`}
            className="border border-white/5 px-4 py-3"
          >
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
              {label}
            </p>
            <p className="mt-1 font-display font-black text-2xl">
              {status.counts?.[key] ?? 0}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
